import { useState, useEffect } from 'react';
import { fetchNearbyEvents } from '../lib/supabase';
import type { Event } from '../types/event';

interface UseEventsParams {
  latitude: number;
  longitude: number;
  radius?: number;
}

export function useEvents({ latitude, longitude, radius = 50000 }: UseEventsParams) {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async (attempt = 0) => {
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setError('Invalid coordinates');
        setIsLoading(false);
        setEvents([]);
        return;
      }

      try {
        if (attempt === 0) setIsLoading(true);
        setError(null);
        
        const data = await fetchNearbyEvents({
          lat: latitude,
          lng: longitude,
          radius_meters: radius
        });
        
        if (isMounted) {
          setEvents(data);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading events:', err);
        if (isMounted) {
          if (attempt < 2) {
            setTimeout(() => { if (isMounted) loadEvents(attempt + 1); }, 2000 * (attempt + 1));
          } else {
            setError('Unable to load events right now. Please try again shortly.');
            setEvents([]);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadEvents();

    const interval = setInterval(() => loadEvents(0), 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [latitude, longitude, radius]);

  return { events, isLoading, error };
}